import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DistDiscountsConfig.css';

export default function DistCartonConversionsConfig() {
  const navigate = useNavigate();

  const [cfg,      setCfg]      = useState({ overrides: {}, cache: {} });
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  const [newSku,    setNewSku]    = useState('');
  const [newInner,  setNewInner]  = useState('');
  const [newMaster, setNewMaster] = useState('');

  function reload() {
    setLoading(true);
    Promise.all([
      axios.get('/api/dist-orders/config/carton-conversions'),
      axios.get('/api/dist-orders/products'),
    ]).then(([cfgRes, prodRes]) => {
      setCfg(cfgRes.data || { overrides: {}, cache: {} });
      setProducts(prodRes.data || []);
      setLoading(false);
    }).catch(err => {
      const status = err.response?.status;
      const detail = err.response?.data?.error;
      setError(
        status === 401
          ? 'Failed to load carton conversions — you may have been signed out. Try refreshing the page.'
          : `Failed to load carton conversions${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`
      );
      setLoading(false);
    });
  }

  useEffect(() => { reload(); }, []);

  function upsertOverride(sku, innerQty, masterQty) {
    if (!sku) return;
    setSaving(true);
    axios.post('/api/dist-orders/config/carton-conversions', {
      sku, inner_qty: innerQty === '' ? null : innerQty, master_qty: masterQty === '' ? null : masterQty,
    }).then(r => {
      setCfg(r.data || { overrides: {}, cache: {} });
      setSaving(false);
    }).catch(() => { setError('Failed to save override'); setSaving(false); });
  }

  function removeOverride(sku) {
    setSaving(true);
    axios.delete(`/api/dist-orders/config/carton-conversions/${encodeURIComponent(sku)}`)
      .then(r => { setCfg(r.data || { overrides: {}, cache: {} }); setSaving(false); })
      .catch(() => { setError('Failed to remove override'); setSaving(false); });
  }

  function addOverride(e) {
    e.preventDefault();
    if (!newSku.trim() || (newInner === '' && newMaster === '')) return;
    upsertOverride(newSku.trim(), newInner, newMaster);
    setNewSku(''); setNewInner(''); setNewMaster('');
  }

  const overrides = cfg.overrides || {};
  const cache     = cfg.cache || {};
  const allSkus   = Array.from(new Set([...Object.keys(overrides), ...Object.keys(cache)])).sort();

  return (
    <div className="ddcfg-page">
      <datalist id="cc-sku-catalog">
        {products.map(p => <option key={p.sku} value={p.sku}>{p.title}</option>)}
      </datalist>
      <div className="ddcfg-header">
        <button className="ddcfg-back" onClick={() => navigate('/draft-orders')}>
          ← Back
        </button>
        <div>
          <h1>Master/Inner Carton Conversions</h1>
          <p className="ddcfg-muted">
            Each distributor line item gets exploded into Master ("MA-&lt;sku&gt;") and Inner ("IN-&lt;sku&gt;") carton
            line items before the draft order is created. The pack size for each tier is derived automatically from
            Shopify pricing — <strong>carton price ÷ individual unit price</strong> — and cached below, re-derived
            automatically whenever the price changes. If that derivation is ever wrong for a SKU, set a manual
            override here — it always wins over the derived value, with no need to wait on a Shopify price fix.
          </p>
        </div>
      </div>

      {error && (
        <div className="ddcfg-error">
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <p className="ddcfg-muted">Loading…</p>
      ) : (
        <>
          {/* Add override */}
          <div className="ddcfg-section">
            <label className="ddcfg-label">Add / update a manual override</label>
            <form className="ddcfg-add-form" onSubmit={addOverride}>
              <input
                className="ddcfg-input"
                list="cc-sku-catalog"
                placeholder="Base SKU"
                value={newSku}
                onChange={e => setNewSku(e.target.value)}
                disabled={saving}
              />
              <input
                className="ddcfg-input"
                placeholder="Inner qty (blank = none)"
                type="number"
                value={newInner}
                onChange={e => setNewInner(e.target.value)}
                disabled={saving}
              />
              <input
                className="ddcfg-input"
                placeholder="Master qty (blank = none)"
                type="number"
                value={newMaster}
                onChange={e => setNewMaster(e.target.value)}
                disabled={saving}
              />
              <button className="ddcfg-btn-primary" type="submit" disabled={saving || !newSku.trim()}>
                Save
              </button>
            </form>
          </div>

          {/* Table */}
          {allSkus.length === 0 ? (
            <p className="ddcfg-muted">No carton conversions derived or overridden yet — they're picked up the first time a PO for that SKU is processed.</p>
          ) : (
            <div className="ddcfg-table-wrap">
              <table className="ddcfg-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Derived Inner / Master</th>
                    <th>Override Inner</th>
                    <th>Override Master</th>
                    <th>Source</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {allSkus.map(sku => {
                    const derived  = cache[sku] || {};
                    const override = overrides[sku];
                    return (
                      <tr key={sku}>
                        <td className="ddcfg-account-tag">{sku}</td>
                        <td>
                          {derived.inner_qty ?? '—'} / {derived.master_qty ?? '—'}
                          {derived.updated_at && (
                            <div className="ddcfg-muted-sm">as of {new Date(derived.updated_at).toLocaleDateString()}</div>
                          )}
                        </td>
                        <td>
                          <input
                            className="ddcfg-input"
                            type="number"
                            defaultValue={override?.inner_qty ?? ''}
                            placeholder="—"
                            disabled={saving}
                            onBlur={e => upsertOverride(sku, e.target.value, override?.master_qty ?? '')}
                          />
                        </td>
                        <td>
                          <input
                            className="ddcfg-input"
                            type="number"
                            defaultValue={override?.master_qty ?? ''}
                            placeholder="—"
                            disabled={saving}
                            onBlur={e => upsertOverride(sku, override?.inner_qty ?? '', e.target.value)}
                          />
                        </td>
                        <td>{override ? 'Manual override' : 'Derived from price'}</td>
                        <td>
                          {override && (
                            <button className="ddcfg-remove" disabled={saving} onClick={() => removeOverride(sku)}>
                              Remove override
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
